import { type ReactNode, useRef } from 'react';

import { useDocumentMeta } from '@hooks/useDocumentMeta';
import { useReveal } from '@hooks/useReveal';
import { MAIN_CONTENT_ID } from '@hooks/useRouteChange';

import styles from './Page.module.scss';

interface PageProps {
  /** Short page name for the tab title. Omit on the home page. */
  readonly title?: string;
  readonly description?: string;
  readonly children: ReactNode;
}

/**
 * Wraps every page: the document's one <main>, its title and description,
 * and scroll reveals for everything inside it.
 *
 * `tabIndex={-1}` lets useRouteChange move focus here after navigation
 * without adding <main> to the tab order.
 */
export const Page = ({ title, description, children }: PageProps) => {
  const ref = useRef<HTMLElement>(null);
  useDocumentMeta(title, description);
  useReveal(ref);

  return (
    <main id={MAIN_CONTENT_ID} ref={ref} tabIndex={-1} className={styles.page}>
      {children}
    </main>
  );
};
